import { type Filter as BaseFilter, FiltersOperator } from 'common/api/resources/fieldFilters';
import { type DefaultFieldNames, IdeaFieldTypes } from 'common/constants/ideas';
import {
  AlphanumericConditions,
  BooleanConditions,
  type ConditionType,
  type CustomFieldFilterConditions,
  DateConditions,
  DropdownConditions,
  InputTypes,
  MultiselectConditions,
  NumericConditions,
  getConditionInputType,
} from 'common/fieldFilters/utils';
import createCustomError from 'common/util/createCustomError';
import isNil from 'common/util/isNil';
import validateInput from 'common/validateInput';

import type { CustomIdeaFieldTypes } from 'common/types/ideas';

export { FiltersOperator };

export const Errors = {
  InvalidFilter: createCustomError('InvalidFilter'),
};

export const isValidConditionForField = (
  fieldType: CustomIdeaFieldTypes,
  condition: CustomFieldFilterConditions
) => {
  switch (fieldType) {
    case IdeaFieldTypes.text:
    case IdeaFieldTypes.multilineText:
    case IdeaFieldTypes.url:
      return AlphanumericConditions.hasOwnProperty(condition);
    case IdeaFieldTypes.boolean:
      return BooleanConditions.hasOwnProperty(condition);
    case IdeaFieldTypes.number:
      return NumericConditions.hasOwnProperty(condition);
    case IdeaFieldTypes.user:
    case IdeaFieldTypes.dropdown:
      return DropdownConditions.hasOwnProperty(condition);
    case IdeaFieldTypes.multiselect:
      return MultiselectConditions.hasOwnProperty(condition);
    case IdeaFieldTypes.date:
      return DateConditions.hasOwnProperty(condition);
  }
};

export const IdeaTypeToConditionTypeMap: Record<IdeaFieldTypes, ConditionType> = {
  [IdeaFieldTypes.text]: 'alphanumeric',
  [IdeaFieldTypes.multilineText]: 'alphanumeric',
  [IdeaFieldTypes.url]: 'alphanumeric',
  [IdeaFieldTypes.boolean]: 'boolean',
  [IdeaFieldTypes.number]: 'numeric',
  [IdeaFieldTypes.user]: 'dropdown',
  [IdeaFieldTypes.dropdown]: 'dropdown',
  [IdeaFieldTypes.multiselect]: 'multiselect',
  [IdeaFieldTypes.date]: 'date',
  [IdeaFieldTypes.formula]: 'numeric',
  [IdeaFieldTypes.score]: 'numeric',
};

export const validateFilter = (filter: Filter): boolean => {
  if (isFilterIdeaCustomField(filter) || isFilterDefaultField(filter)) {
    // Validate the values against the conditions
    const inputType = getConditionInputType(filter.condition);

    // Don't validate dropdowns, since they are validated on the backend
    if (inputType === InputTypes.number) {
      return (
        validateInput.integer(filter.value?.value) &&
        !!filter.value?.value &&
        filter.value?.value?.length >= 1
      );
    } else if (inputType === InputTypes.text) {
      return (
        validateInput.string(filter.value?.value) &&
        !!filter.value?.value &&
        filter.value?.value?.length >= 3
      );
    } else if (inputType === InputTypes.dropdown) {
      return (
        validateInput.string(filter.value?.value) &&
        !!filter.value?.value &&
        filter.value?.value?.length >= 1
      );
    } else if (inputType === InputTypes.multiselect) {
      return validateInput.arrayOf(validateInput.string)(filter.value?.value);
    } else if (inputType === InputTypes.dateRange) {
      return validateInput.arrayOf(validateInput.primitives.isoString, 2, 2)(filter.value?.value);
    } else if (inputType === null) {
      return filter.value?.value === '' || isNil(filter.value?.value);
    }

    return (
      validateInput.views.filters(filter.condition) && validateInput.string(filter.value?.fieldID)
    );
  } else {
    throw new Errors.InvalidFilter('invalid filter', {
      filter,
    });
  }
};

export enum Resource {
  ideaDefaultField = 'ideaDefaultField',
  ideaCustomField = 'ideaCustomField',
}

export type Filter = BaseFilter<Resource>;

export interface ViewFilter<
  T extends Extract<Resource, Resource.ideaCustomField | Resource.ideaDefaultField>
> extends BaseFilter<T> {
  condition: CustomFieldFilterConditions;
  value: {
    fieldID: string;
    value: string | string[] | null;
  };
}

export type IdeaCustomFieldFilter = ViewFilter<Resource.ideaCustomField>;
export interface DefaultFieldFilter extends ViewFilter<Resource.ideaDefaultField> {
  value: {
    fieldID: DefaultFieldNames;
    value: string | string[] | null;
  };
}

export const isFilterIdeaCustomField = (filter: Filter): filter is IdeaCustomFieldFilter =>
  filter.resource === Resource.ideaCustomField;

export const isFilterDefaultField = (filter: Filter): filter is DefaultFieldFilter =>
  filter.resource === Resource.ideaDefaultField;
